import React, { useEffect, useMemo, useState } from "react";
import {
  Sparkle, Trash, Plus, X, Timer, MagnifyingGlass, ClipboardText, CheckCircle,
  Users, UserPlus, PaperPlaneTilt, Calendar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const SPORTS = [
  { key: "", label: "ALL SPORTS" },
  { key: "football", label: "Football" },
  { key: "basketball", label: "Basketball" },
  { key: "cricket", label: "Cricket" },
  { key: "tennis", label: "Tennis" },
  { key: "swimming", label: "Swimming" },
  { key: "athletics", label: "Athletics" },
  { key: "hockey", label: "Hockey" },
  { key: "rugby", label: "Rugby" },
  { key: "badminton", label: "Badminton" },
  { key: "volleyball", label: "Volleyball" },
  { key: "boxing", label: "Boxing" },
  { key: "kabaddi", label: "Kabaddi" },
];

const INTENSITIES = [
  { key: "low", label: "LOW · Recovery / technique focus" },
  { key: "moderate", label: "MODERATE · Standard training" },
  { key: "high", label: "HIGH · Match-day sharpness" },
];

function PhaseTone(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("warm")) return "border-amber-300 text-amber-700 bg-amber-50";
  if (n.includes("cool")) return "border-cyan-300 text-cyan-700 bg-cyan-50";
  return "border-emerald-300 text-emerald-700 bg-emerald-50";
}

function PlanView({ plan }) {
  if (!plan) return null;
  return (
    <div className="space-y-4" data-testid="session-plan-view">
      <div className="border border-slate-900 bg-slate-900 text-slate-50 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-tiny text-amber-400 mb-1">// {(plan.sport || "").replace(/_/g, " ").toUpperCase()} · {(plan.intensity || "moderate").toUpperCase()}</div>
            <div className="font-heading text-2xl">{plan.session_title || "Session"}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-3xl text-amber-400 tabular-nums flex items-center gap-1">
              <Timer size={18} weight="fill" /> {plan.duration_min}
              <span className="text-sm text-slate-400 ml-1">MIN</span>
            </div>
          </div>
        </div>
        {plan.progression_note && (
          <div className="mt-3 text-xs text-slate-300 italic">{plan.progression_note}</div>
        )}
      </div>

      {(plan.phases || []).map((ph, i) => (
        <div key={i} className={`border p-3 ${PhaseTone(ph.name)}`} data-testid={`session-phase-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-heading text-sm">{(ph.name || "").toUpperCase()}</div>
            <div className="text-xs tabular-nums">{ph.duration_min} MIN · {(ph.activities || []).length} activities</div>
          </div>
          <div className="bg-white border border-slate-200">
            {(ph.activities || []).map((a, j) => (
              <div key={j} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 last:border-0 text-xs items-center">
                <div className="col-span-2 tabular-nums text-slate-500 font-heading">{a.duration_min} min</div>
                <div className="col-span-4 font-heading text-slate-900 truncate">{a.drill_name}</div>
                <div className="col-span-6 text-slate-600">{a.note || ""}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {plan.coaching_reminders?.length > 0 && (
        <div className="border border-slate-200 bg-white p-3">
          <div className="label-tiny text-cyan-700 mb-2">// COACHING REMINDERS</div>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            {plan.coaching_reminders.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function SportsSessions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [assignCounts, setAssignCounts] = useState({}); // session_id -> count

  // Assign modal
  const [assignFor, setAssignFor] = useState(null); // session object
  const [rosterAthletes, setRosterAthletes] = useState([]);
  const [rosterTeams, setRosterTeams] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [assignMode, setAssignMode] = useState("athletes"); // athletes | team
  const [selectedAthletes, setSelectedAthletes] = useState({});
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [assignForm, setAssignForm] = useState({ scheduled_date: "", scheduled_time: "", notes: "" });
  const [recurring, setRecurring] = useState({ enabled: false, weekday: 1, weeks_count: 8 });
  const [assigning, setAssigning] = useState(false);
  const [existingAssignments, setExistingAssignments] = useState([]);

  // Builder modal state
  const [showBuilder, setShowBuilder] = useState(false);
  const [step, setStep] = useState(1);
  const [availableDrills, setAvailableDrills] = useState([]);
  const [drillsLoading, setDrillsLoading] = useState(false);
  const [drillFilter, setDrillFilter] = useState({ sport: "football", q: "" });
  const [picked, setPicked] = useState({});
  const [config, setConfig] = useState({ duration_min: 60, intensity: "moderate", focus: "" });
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/sports-coach/sessions")
      .then(async ({ data }) => {
        const items = data.items || [];
        setRows(items);
        // Fetch assignment counts in parallel (best-effort)
        const counts = {};
        await Promise.all(items.map(async (s) => {
          try {
            const { data } = await api.get(`/sports-coach/sessions/${s.session_id}/assignments`);
            counts[s.session_id] = data.total || 0;
          } catch { counts[s.session_id] = 0; }
        }));
        setAssignCounts(counts);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // ---- Assign flow ----
  const openAssign = async (session) => {
    setAssignFor(session);
    setSelectedAthletes({});
    setSelectedTeamId("");
    setAssignMode("athletes");
    setAssignForm({
      scheduled_date: new Date().toISOString().slice(0, 10),
      scheduled_time: "",
      notes: "",
    });
    setRecurring({ enabled: false, weekday: 1, weeks_count: 8 });
    setRosterLoading(true);
    try {
      const [a, t, ex] = await Promise.all([
        api.get("/sports-coach/roster/athletes"),
        api.get("/sports-coach/roster/teams"),
        api.get(`/sports-coach/sessions/${session.session_id}/assignments`),
      ]);
      setRosterAthletes(a.data.items || []);
      setRosterTeams(t.data.items || []);
      setExistingAssignments(ex.data.items || []);
    } finally {
      setRosterLoading(false);
    }
  };

  const submitAssign = async () => {
    if (!assignFor) return;
    const athlete_ids = assignMode === "athletes"
      ? Object.keys(selectedAthletes).filter((k) => selectedAthletes[k])
      : [];
    const team_id = assignMode === "team" ? selectedTeamId : null;
    if (assignMode === "athletes" && athlete_ids.length === 0) {
      toast.error("Pick at least one athlete");
      return;
    }
    if (assignMode === "team" && !team_id) {
      toast.error("Pick a team");
      return;
    }
    if (!assignForm.scheduled_date) {
      toast.error("Pick a date");
      return;
    }
    setAssigning(true);
    try {
      const body = {
        athlete_ids, team_id, ...assignForm,
      };
      if (recurring.enabled) {
        body.recurring = true;
        body.weekday = Number(recurring.weekday);
        body.weeks_count = Number(recurring.weeks_count);
        body.start_from = assignForm.scheduled_date;
      }
      const { data } = await api.post(
        `/sports-coach/sessions/${assignFor.session_id}/assign`,
        body,
      );
      let msg;
      if (recurring.enabled && data.assigned > 0) {
        msg = `Rolled out ${data.assigned} sessions across ${data.athletes_count} athlete${data.athletes_count !== 1 ? "s" : ""} × ${data.dates_count} weeks`;
      } else if (data.assigned > 0) {
        msg = `Assigned to ${data.assigned} athlete${data.assigned !== 1 ? "s" : ""}${data.skipped_duplicates ? ` · ${data.skipped_duplicates} already scheduled` : ""}`;
      } else {
        msg = "No new assignments — everyone already has these dates";
      }
      toast.success(msg);
      setAssignFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed");
    } finally {
      setAssigning(false);
    }
  };

  const unassign = async (assignmentId) => {
    if (!window.confirm("Remove this assignment?")) return;
    await api.delete(`/sports-coach/assignments/${assignmentId}`);
    toast.success("Removed");
    setExistingAssignments((prev) => prev.filter((r) => r.assignment_id !== assignmentId));
    if (assignFor) {
      setAssignCounts((c) => ({ ...c, [assignFor.session_id]: Math.max(0, (c[assignFor.session_id] || 1) - 1) }));
    }
  };

  const unassignSeries = async (groupId) => {
    const inGroup = existingAssignments.filter((r) => r.recurrence_group_id === groupId);
    if (!window.confirm(`Remove the entire series (${inGroup.length} sessions)?`)) return;
    const { data } = await api.delete(`/sports-coach/assignments/group/${groupId}`);
    toast.success(`Removed ${data.deleted} sessions from the series`);
    setExistingAssignments((prev) => prev.filter((r) => r.recurrence_group_id !== groupId));
    if (assignFor) {
      setAssignCounts((c) => ({ ...c, [assignFor.session_id]: Math.max(0, (c[assignFor.session_id] || 0) - data.deleted) }));
    }
  };

  const openBuilder = () => {
    setShowBuilder(true);
    setStep(1);
    setPicked({});
    setPreview(null);
    setTitle("");
    setConfig({ duration_min: 60, intensity: "moderate", focus: "" });
  };

  // Load drills when builder opens / filter changes
  useEffect(() => {
    if (!showBuilder || step !== 1) return;
    setDrillsLoading(true);
    const params = new URLSearchParams();
    if (drillFilter.sport) params.set("sport", drillFilter.sport);
    if (drillFilter.q) params.set("q", drillFilter.q);
    api.get(`/sports-coach/drills?${params.toString()}`)
      .then(({ data }) => setAvailableDrills(data.items || []))
      .finally(() => setDrillsLoading(false));
  }, [showBuilder, step, drillFilter.sport]);

  const pickedIds = useMemo(() => Object.keys(picked).filter((k) => picked[k]), [picked]);
  const filteredDrills = useMemo(() => {
    if (!drillFilter.q) return availableDrills;
    const q = drillFilter.q.toLowerCase();
    return availableDrills.filter((d) =>
      (d.name || "").toLowerCase().includes(q) ||
      (d.category || "").toLowerCase().includes(q) ||
      (d.tags || []).some((t) => (t || "").toLowerCase().includes(q))
    );
  }, [availableDrills, drillFilter.q]);

  const generate = async () => {
    if (pickedIds.length < 2) { toast.error("Pick at least 2 drills"); return; }
    setGenerating(true);
    setPreview(null);
    try {
      const { data } = await api.post("/sports-coach/sessions/generate", {
        drill_ids: pickedIds,
        sport: drillFilter.sport || "general",
        duration_min: Number(config.duration_min) || 60,
        intensity: config.intensity,
        focus: config.focus,
      });
      setPreview(data);
      setTitle(data.plan?.session_title || "Session");
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!preview) return;
    if (!title.trim()) { toast.error("Give the session a title"); return; }
    setSaving(true);
    try {
      await api.post("/sports-coach/sessions", {
        title: title.trim(),
        sport: preview.sport,
        duration_min: preview.plan.duration_min,
        intensity: preview.plan.intensity,
        focus: preview.focus || config.focus,
        drill_ids: preview.drill_ids,
        plan: preview.plan,
      });
      toast.success("Session saved");
      setShowBuilder(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this session plan?")) return;
    await api.delete(`/sports-coach/sessions/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="sc-sessions">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <div className="label-tiny text-slate-500">// AI SESSION PLANNER</div>
          <div className="text-sm text-slate-600 flex items-center gap-1.5">
            <Sparkle size={12} className="text-cyan-500" />
            Pick 2–10 drills · Gemini stitches a warm-up → main → cool-down plan with timings & cues.
          </div>
        </div>
        <button onClick={openBuilder} className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs label-tiny tracking-wider px-4 py-2" data-testid="sc-sessions-new-btn">
          <Sparkle size={14} weight="fill" /> NEW SESSION PLAN
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-8 text-center" data-testid="sc-sessions-empty">
          <ClipboardText size={28} weight="duotone" className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No session plans yet. Click <b>NEW SESSION PLAN</b> to generate one with AI.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((s) => {
            const phases = s.plan?.phases || [];
            const count = assignCounts[s.session_id] || 0;
            return (
              <div key={s.session_id} className="border border-slate-200 bg-white p-4 flex flex-col gap-2" data-testid={`sc-session-card-${s.session_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading text-base truncate">{s.title}</div>
                    <div className="text-[10px] tracking-widest text-cyan-700 mt-1">
                      {(s.sport || "").replace(/_/g, " ").toUpperCase()} · {(s.intensity || "").toUpperCase()} · {s.duration_min} MIN
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {count > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5" data-testid={`sc-session-assigncount-${s.session_id}`}>
                        <Users size={10} weight="fill" /> {count}
                      </span>
                    )}
                    <button onClick={() => remove(s.session_id)} className="text-slate-400 hover:text-red-600" data-testid={`sc-session-delete-${s.session_id}`}><Trash size={16} /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {phases.map((p, i) => (
                    <span key={i} className={`text-[10px] px-1.5 py-0.5 border ${PhaseTone(p.name)}`}>
                      {(p.name || "").toUpperCase()} · {p.duration_min}m
                    </span>
                  ))}
                </div>
                {s.focus && <div className="text-xs text-slate-500 line-clamp-1">Focus: {s.focus}</div>}
                <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-slate-100">
                  <button onClick={() => setDetail(s)} className="text-cyan-600 hover:text-cyan-700 text-xs label-tiny" data-testid={`sc-session-view-${s.session_id}`}>
                    VIEW PLAN →
                  </button>
                  <button
                    onClick={() => openAssign(s)}
                    className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs label-tiny tracking-wider px-3 py-1"
                    data-testid={`sc-session-assign-${s.session_id}`}
                  >
                    <PaperPlaneTilt size={12} weight="fill" /> ASSIGN
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assign modal */}
      {assignFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setAssignFor(null)} data-testid="sc-assign-modal">
          <div className="bg-white max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <div className="label-tiny text-emerald-700">// ASSIGN TO ROSTER</div>
                <div className="font-heading text-xl">{assignFor.title}</div>
                <div className="text-[10px] tracking-widest text-slate-500 mt-1">
                  {(assignFor.sport || "").replace(/_/g, " ").toUpperCase()} · {assignFor.duration_min} MIN
                </div>
              </div>
              <button onClick={() => setAssignFor(null)} data-testid="sc-assign-close"><X size={20} /></button>
            </div>

            <div className="p-4 flex-1 overflow-y-auto space-y-4">
              {/* Mode toggle */}
              <div className="flex gap-1 border border-slate-300 w-fit" data-testid="sc-assign-mode">
                <button
                  onClick={() => setAssignMode("athletes")}
                  className={`px-3 py-1.5 text-xs label-tiny tracking-wider ${assignMode === "athletes" ? "bg-cyan-600 text-white" : "bg-white text-slate-600"}`}
                  data-testid="sc-assign-mode-athletes"
                >
                  <UserPlus size={12} className="inline mr-1" /> BY ATHLETE
                </button>
                <button
                  onClick={() => setAssignMode("team")}
                  className={`px-3 py-1.5 text-xs label-tiny tracking-wider ${assignMode === "team" ? "bg-cyan-600 text-white" : "bg-white text-slate-600"}`}
                  data-testid="sc-assign-mode-team"
                >
                  <Users size={12} className="inline mr-1" /> BY TEAM
                </button>
              </div>

              {/* Selection area */}
              {rosterLoading ? (
                <div className="text-sm text-slate-500">Loading your roster…</div>
              ) : assignMode === "athletes" ? (
                rosterAthletes.length === 0 ? (
                  <div className="border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="sc-assign-athletes-empty">
                    You have no athletes linked yet. Invite one from the Athletes tab first.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1" data-testid="sc-assign-athlete-list">
                    {rosterAthletes.map((a) => {
                      const on = !!selectedAthletes[a.client_id];
                      return (
                        <button
                          key={a.client_id}
                          onClick={() => setSelectedAthletes((p) => ({ ...p, [a.client_id]: !p[a.client_id] }))}
                          className={`text-left border p-2 flex items-center gap-2 ${on ? "border-cyan-500 bg-cyan-50" : "border-slate-200 bg-white hover:border-cyan-300"}`}
                          data-testid={`sc-assign-athlete-${a.client_id}`}
                        >
                          <div className="w-8 h-8 bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-heading text-slate-500 shrink-0">
                            {(a.name || "A").slice(0, 1).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-heading text-sm truncate">{a.name}</div>
                            <div className="text-[10px] text-slate-500 truncate">{a.email}</div>
                          </div>
                          {on && <CheckCircle size={16} weight="fill" className="text-cyan-600 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )
              ) : (
                rosterTeams.length === 0 ? (
                  <div className="border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="sc-assign-teams-empty">
                    You have no teams yet. Create one from the Teams page first.
                  </div>
                ) : (
                  <div className="space-y-1" data-testid="sc-assign-team-list">
                    {rosterTeams.map((t) => (
                      <label key={t.team_id} className={`flex items-center gap-2 border p-2 cursor-pointer ${selectedTeamId === t.team_id ? "border-cyan-500 bg-cyan-50" : "border-slate-200"}`} data-testid={`sc-assign-team-${t.team_id}`}>
                        <input type="radio" name="team" checked={selectedTeamId === t.team_id} onChange={() => setSelectedTeamId(t.team_id)} className="accent-cyan-600" />
                        <div className="flex-1 min-w-0">
                          <div className="font-heading text-sm">{t.name}</div>
                          {t.description && <div className="text-xs text-slate-500 truncate">{t.description}</div>}
                        </div>
                        <span className="text-[10px] tracking-widest text-slate-500">{t.member_count} ATH</span>
                      </label>
                    ))}
                  </div>
                )
              )}

              {/* Schedule + notes */}
              <div className="border-t border-slate-100 pt-3">
                {/* Recurring toggle */}
                <div className="flex items-center gap-2 mb-3">
                  <button
                    onClick={() => setRecurring({ ...recurring, enabled: !recurring.enabled })}
                    role="switch"
                    aria-checked={recurring.enabled}
                    className={`relative inline-flex h-5 w-9 items-center transition-colors ${recurring.enabled ? "bg-cyan-600" : "bg-slate-300"}`}
                    data-testid="sc-assign-recurring-toggle"
                  >
                    <span className={`inline-block h-4 w-4 bg-white transform transition-transform ${recurring.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                  </button>
                  <label className="label-tiny text-slate-700">MAKE IT RECURRING</label>
                  {recurring.enabled && (
                    <span className="text-[10px] text-cyan-700 bg-cyan-50 border border-cyan-200 px-1.5 py-0.5">
                      🔁 {recurring.weeks_count}-WEEK BLOCK
                    </span>
                  )}
                </div>

                {!recurring.enabled ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="label-tiny block mb-1 text-slate-600">DATE</label>
                      <input type="date" value={assignForm.scheduled_date} onChange={(e) => setAssignForm({ ...assignForm, scheduled_date: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-assign-date" />
                    </div>
                    <div>
                      <label className="label-tiny block mb-1 text-slate-600">TIME (OPTIONAL)</label>
                      <input type="time" value={assignForm.scheduled_time} onChange={(e) => setAssignForm({ ...assignForm, scheduled_time: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-assign-time" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="label-tiny block mb-1 text-slate-600">START FROM</label>
                        <input type="date" value={assignForm.scheduled_date} onChange={(e) => setAssignForm({ ...assignForm, scheduled_date: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-assign-start" />
                      </div>
                      <div>
                        <label className="label-tiny block mb-1 text-slate-600">WEEKDAY</label>
                        <select value={recurring.weekday} onChange={(e) => setRecurring({ ...recurring, weekday: Number(e.target.value) })} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-assign-weekday">
                          {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => (
                            <option key={i} value={i}>{d}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="label-tiny block mb-1 text-slate-600">FOR HOW MANY WEEKS?</label>
                        <input type="number" min={1} max={52} value={recurring.weeks_count} onChange={(e) => setRecurring({ ...recurring, weeks_count: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-assign-weeks" />
                      </div>
                    </div>
                    <div>
                      <label className="label-tiny block mb-1 text-slate-600">TIME (OPTIONAL)</label>
                      <input type="time" value={assignForm.scheduled_time} onChange={(e) => setAssignForm({ ...assignForm, scheduled_time: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-assign-time-recurring" />
                    </div>
                    <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 p-2">
                      This will create <span className="font-heading text-cyan-700">{recurring.weeks_count} occurrences</span> — one every {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][recurring.weekday]} — for every selected athlete{assignMode === "team" ? " on the team" : ""}.
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="label-tiny block mb-1 text-slate-600">NOTES FOR ATHLETES (OPTIONAL)</label>
                <textarea rows={2} value={assignForm.notes} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="e.g. Come warmed up, bring your Vaporfly's…" data-testid="sc-assign-notes" />
              </div>

              {/* Existing assignments list — group by recurrence_group_id */}
              {existingAssignments.length > 0 && (() => {
                const groups = {};
                const singles = [];
                for (const r of existingAssignments) {
                  if (r.recurrence_group_id) {
                    (groups[r.recurrence_group_id] ||= []).push(r);
                  } else {
                    singles.push(r);
                  }
                }
                return (
                  <div className="border-t border-slate-100 pt-3">
                    <div className="label-tiny text-slate-500 mb-2">// EXISTING ASSIGNMENTS ({existingAssignments.length})</div>
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {Object.entries(groups).map(([gid, list]) => {
                        const athletes = [...new Set(list.map((r) => r.athlete_name || r.athlete_id))];
                        const first = list.reduce((a, b) => (a.scheduled_date < b.scheduled_date ? a : b));
                        return (
                          <div key={gid} className="border border-cyan-200 bg-cyan-50 p-2 text-xs" data-testid={`sc-series-${gid}`}>
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="min-w-0 flex-1">
                                <div className="font-heading text-cyan-800 flex items-center gap-1">
                                  🔁 SERIES · {list.length} SESSIONS · {athletes.length} ATH{athletes.length !== 1 ? "S" : ""}
                                </div>
                                <div className="text-slate-500 text-[10px] mt-0.5">Starts {first.scheduled_date}{first.scheduled_time ? ` ${first.scheduled_time}` : ""} · {athletes.slice(0, 3).join(", ")}{athletes.length > 3 ? ` +${athletes.length - 3}` : ""}</div>
                              </div>
                              <button onClick={() => unassignSeries(gid)} className="text-red-600 hover:text-red-800 text-[10px] label-tiny tracking-wider" data-testid={`sc-unassign-series-${gid}`}>
                                DELETE SERIES
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {singles.map((r) => (
                        <div key={r.assignment_id} className="flex items-center justify-between gap-2 border border-slate-200 px-2 py-1.5 text-xs" data-testid={`sc-existing-${r.assignment_id}`}>
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            <Calendar size={12} className="text-slate-400" />
                            <span className="font-heading truncate">{r.athlete_name || r.athlete_id}</span>
                            <span className="text-slate-500 tabular-nums">{r.scheduled_date}{r.scheduled_time ? ` ${r.scheduled_time}` : ""}</span>
                            {r.status !== "assigned" && <span className="text-[9px] bg-emerald-100 text-emerald-700 border border-emerald-300 px-1">{r.status.toUpperCase()}</span>}
                          </div>
                          <button onClick={() => unassign(r.assignment_id)} className="text-slate-400 hover:text-red-600" data-testid={`sc-unassign-${r.assignment_id}`}><Trash size={12} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-3 border-t border-slate-200 bg-slate-50">
              <button onClick={() => setAssignFor(null)} className="text-xs label-tiny tracking-widest text-slate-500 hover:text-slate-900 px-3 py-1.5">
                CANCEL
              </button>
              <button
                onClick={submitAssign}
                disabled={assigning}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs label-tiny tracking-wider px-4 py-1.5 flex items-center gap-2"
                data-testid="sc-assign-submit"
              >
                <PaperPlaneTilt size={12} weight="fill" /> {assigning ? "ASSIGNING…" : "ASSIGN"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Builder modal */}
      {showBuilder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowBuilder(false)} data-testid="sc-session-builder">
          <div className="bg-white max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <div className="label-tiny text-cyan-700">// SESSION PLANNER · STEP {step} OF 3</div>
                <div className="font-heading text-xl">
                  {step === 1 && "Pick your drills"}
                  {step === 2 && "Configure the session"}
                  {step === 3 && "Review & save"}
                </div>
              </div>
              <button onClick={() => setShowBuilder(false)} data-testid="sc-session-builder-close"><X size={20} /></button>
            </div>

            {step === 1 && (
              <div className="p-4 flex-1 overflow-y-auto" data-testid="sc-step-1">
                <div className="flex gap-2 mb-3 items-center flex-wrap">
                  <div className="flex items-center gap-2 flex-1 min-w-[200px] border border-slate-300 px-2 py-1 bg-white">
                    <MagnifyingGlass size={14} className="text-slate-400" />
                    <input value={drillFilter.q} onChange={(e) => setDrillFilter({ ...drillFilter, q: e.target.value })} placeholder="Search drills…" className="flex-1 text-sm focus:outline-none" data-testid="sc-builder-search" />
                  </div>
                  <select value={drillFilter.sport} onChange={(e) => setDrillFilter({ ...drillFilter, sport: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-xs bg-white" data-testid="sc-builder-sport">
                    {SPORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                  <div className="text-xs text-slate-500 ml-auto">{pickedIds.length} / 10 picked</div>
                </div>

                {drillsLoading ? (
                  <div className="text-sm text-slate-500">Loading drills…</div>
                ) : filteredDrills.length === 0 ? (
                  <div className="text-sm text-slate-500 italic p-6 text-center">No drills match those filters.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {filteredDrills.map((d) => {
                      const on = !!picked[d.drill_id];
                      const canPick = on || pickedIds.length < 10;
                      return (
                        <button
                          key={d.drill_id}
                          disabled={!canPick}
                          onClick={() => setPicked((p) => ({ ...p, [d.drill_id]: !p[d.drill_id] }))}
                          className={`text-left border p-2 transition-colors ${on ? "border-cyan-500 bg-cyan-50" : canPick ? "border-slate-200 bg-white hover:border-cyan-300" : "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"}`}
                          data-testid={`sc-builder-drill-${d.drill_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="font-heading text-sm truncate">{d.name}</div>
                              <div className="text-[10px] tracking-widest text-slate-500 mt-1">
                                {(d.category || "").toUpperCase()} · {(d.difficulty || "").toUpperCase()} · {d.duration_min || "?"}m
                              </div>
                            </div>
                            {on && <CheckCircle size={16} weight="fill" className="text-cyan-600 shrink-0" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="p-4 flex-1 overflow-y-auto space-y-4" data-testid="sc-step-2">
                <div className="border border-cyan-300 bg-cyan-50 p-3 text-sm">
                  <div className="label-tiny text-cyan-700 mb-1">// SELECTED · {pickedIds.length} DRILLS</div>
                  <div className="text-xs text-slate-700">
                    {availableDrills.filter((d) => picked[d.drill_id]).map((d) => d.name).join(" · ")}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="label-tiny block mb-1 text-slate-600">TOTAL DURATION (MIN)</label>
                    <input type="number" min={20} max={180} value={config.duration_min} onChange={(e) => setConfig({ ...config, duration_min: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-builder-duration" />
                    <div className="text-[10px] text-slate-500 mt-1">20 – 180 minutes</div>
                  </div>
                  <div>
                    <label className="label-tiny block mb-1 text-slate-600">INTENSITY</label>
                    <select value={config.intensity} onChange={(e) => setConfig({ ...config, intensity: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-builder-intensity">
                      {INTENSITIES.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="label-tiny block mb-1 text-slate-600">COACH FOCUS (optional)</label>
                  <textarea rows={3} value={config.focus} onChange={(e) => setConfig({ ...config, focus: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="e.g. Match-day sharpness, work on pressing triggers, U-15 age group…" data-testid="sc-builder-focus" />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="p-4 flex-1 overflow-y-auto space-y-3" data-testid="sc-step-3">
                {generating ? (
                  <div className="text-center py-10 text-sm text-slate-600 flex flex-col items-center gap-2">
                    <Sparkle size={28} weight="fill" className="text-cyan-500 animate-pulse" />
                    Composing session with Gemini 3.1 Pro…
                  </div>
                ) : preview ? (
                  <>
                    <div>
                      <label className="label-tiny block mb-1 text-slate-600">SESSION TITLE</label>
                      <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white font-heading" data-testid="sc-builder-title" />
                    </div>
                    <PlanView plan={preview.plan} />
                  </>
                ) : (
                  <div className="text-sm text-slate-500 italic">No plan yet.</div>
                )}
              </div>
            )}

            {/* Footer nav */}
            <div className="flex items-center justify-between p-3 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => step > 1 ? setStep(step - 1) : setShowBuilder(false)}
                className="text-xs label-tiny tracking-widest text-slate-500 hover:text-slate-900 px-3 py-1.5"
                data-testid="sc-builder-back"
              >
                {step === 1 ? "CANCEL" : "← BACK"}
              </button>
              {step === 1 && (
                <button
                  disabled={pickedIds.length < 2}
                  onClick={() => setStep(2)}
                  className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-xs label-tiny tracking-wider px-4 py-1.5"
                  data-testid="sc-builder-next-1"
                >
                  NEXT · {pickedIds.length} PICKED →
                </button>
              )}
              {step === 2 && (
                <button
                  onClick={() => { setStep(3); generate(); }}
                  disabled={generating}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs label-tiny tracking-wider px-4 py-1.5 flex items-center gap-2"
                  data-testid="sc-builder-generate"
                >
                  <Sparkle size={12} weight="fill" /> GENERATE
                </button>
              )}
              {step === 3 && (
                <button
                  onClick={save}
                  disabled={!preview || saving}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs label-tiny tracking-wider px-4 py-1.5"
                  data-testid="sc-builder-save"
                >
                  {saving ? "SAVING…" : "SAVE PLAN"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setDetail(null)} data-testid="sc-session-detail">
          <div className="bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <div>
                <div className="font-heading text-xl">{detail.title}</div>
                <div className="text-[10px] tracking-widest text-cyan-700 mt-1">
                  {(detail.sport || "").replace(/_/g, " ").toUpperCase()} · {(detail.intensity || "").toUpperCase()} · {detail.duration_min} MIN
                </div>
              </div>
              <button onClick={() => setDetail(null)} data-testid="sc-session-detail-close"><X size={20} /></button>
            </div>
            <div className="p-4">
              <PlanView plan={detail.plan} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
